import React, { createContext, useState, useContext, useEffect } from 'react';
import { authService, userService } from '../services/api';
import i18n from '../i18n';
import toast from 'react-hot-toast';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  useEffect(() => {
    if (token) {
      fetchCurrentUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchCurrentUser = async () => {
    try {
      const response = await userService.getCurrentUser();
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (username, password) => {
    try {
      const formData = new FormData();
      formData.append('username', username);
      formData.append('password', password);

      const response = await authService.login(formData);
      const { access_token } = response.data;

      localStorage.setItem('token', access_token);
      setToken(access_token);

      await fetchCurrentUser();
      toast.success('Login successful!');
      return true;
    } catch (error) {
      let message = 'Login failed';
      if (error.response?.data?.detail) {
        if (Array.isArray(error.response.data.detail)) {
          // Handle FastAPI validation errors
          message = error.response.data.detail.map(err => err.msg).join(', ');
        } else {
          message = error.response.data.detail;
        }
      }
      toast.error(message);
      return false;
    }
  };

  /**
   * Entrada amb el codi imprès (equips).
   *
   * Retorna el destí que ha calculat el servidor —on ha d'aterrar aquest equip—
   * en comptes de portar sempre al tauler. Amb un examen en marxa, això és la
   * diferència entre tornar on eres i haver de navegar-hi amb el rellotge
   * corrent.
   */
  const entraAmbCodi = async (codi) => {
    try {
      const response = await authService.entraAmbCodi(codi);
      const { access_token, desti } = response.data;

      localStorage.setItem('token', access_token);
      setToken(access_token);

      await fetchCurrentUser();
      return desti || '/';
    } catch (error) {
      // Vegeu Login.js: un 429 no és un codi dolent, i dir-ho malament el dia de
      // l'examen envia la classe sencera a aixecar la mà.
      if (error.response?.status === 429) {
        toast.error(i18n.t('auth.masaPeticions'), { duration: 8000 });
        return null;
      }
      const detall = error.response?.data?.detail;
      toast.error(typeof detall === 'string' ? detall : i18n.t('auth.codiNoValid'));
      return null;
    }
  };

  const register = async (userData) => {
    try {
      await authService.register(userData);
      toast.success('Registration successful! Please wait for approval.');
      return true;
    } catch (error) {
      let message = 'Registration failed';
      if (error.response?.data?.detail) {
        if (Array.isArray(error.response.data.detail)) {
          // Handle FastAPI validation errors
          message = error.response.data.detail.map(err => err.msg).join(', ');
        } else {
          message = error.response.data.detail;
        }
      }
      toast.error(message);
      return false;
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setToken(null);
    setUser(null);
  };

  const value = {
    user,
    token,
    loading,
    login,
    entraAmbCodi,
    register,
    logout,
    isInstructor: user?.role === 'instructor',
    isAnalyst: user?.role === 'analyst',
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};